import type { SiteChatLang } from './types';

/** Composer chrome of the emoji picker and voice messages, per widget language (see `ui.ts`). */
export type SiteChatMediaUi = {
    emoji: string;
    emojiGroups: [string, string, string, string, string, string, string, string];
    record: string;
    recordCancelHint: string;
    recordSlideHint: string;
    recordCancel: string;
    recordStop: string;
    recordHold: string;
    micDenied: string;
    voiceFailed: string;
    deleteVoice: string;
    play: string;
    pause: string;
    voiceMessage: string;
    retryUpload: string;
    openPicture: string;
    download: string;
    previous: string;
    next: string;
    /** Items of the paperclip menu, shown when the browser can capture the screen. */
    attachFile: string;
    screenshot: string;
    screenshotFailed: string;
};

export const MEDIA_UI: Record<SiteChatLang, SiteChatMediaUi> = {
    en: {
        emoji: 'Emoji',
        emojiGroups: ['Smileys', 'People', 'Animals and nature', 'Food', 'Activities', 'Travel', 'Objects', 'Symbols'],
        record: 'Hold to record a voice message',
        recordCancelHint: 'Release outside this field to cancel',
        recordSlideHint: 'Slide left to cancel',
        recordCancel: 'Cancel',
        recordStop: 'Stop recording',
        recordHold: 'Hold the button to record a voice message',
        micDenied: 'Microphone not found or not allowed',
        voiceFailed: 'Voice messages are turned off',
        deleteVoice: 'Delete recording',
        play: 'Play',
        pause: 'Pause',
        voiceMessage: 'Voice message',
        retryUpload: 'Upload again',
        openPicture: 'Open picture',
        download: 'Download',
        previous: 'Previous',
        next: 'Next',
        attachFile: 'File',
        screenshot: 'Screenshot',
        screenshotFailed: 'Could not take a screenshot',
    },
    ru: {
        emoji: 'Эмодзи',
        emojiGroups: ['Смайлы', 'Люди', 'Животные и природа', 'Еда', 'Занятия', 'Путешествия', 'Предметы', 'Символы'],
        record: 'Удерживайте, чтобы записать голосовое',
        recordCancelHint: 'Отпустите за пределами поля, чтобы отменить',
        recordSlideHint: 'Проведите влево, чтобы отменить',
        recordCancel: 'Отмена',
        recordStop: 'Остановить запись',
        recordHold: 'Удерживайте кнопку, чтобы записать голосовое',
        micDenied: 'Микрофон не найден или доступ к нему запрещён',
        voiceFailed: 'Голосовые сообщения отключены',
        deleteVoice: 'Удалить запись',
        play: 'Воспроизвести',
        pause: 'Пауза',
        voiceMessage: 'Голосовое сообщение',
        retryUpload: 'Загрузить ещё раз',
        openPicture: 'Открыть картинку',
        download: 'Скачать',
        previous: 'Назад',
        next: 'Вперёд',
        attachFile: 'Файл',
        screenshot: 'Снимок экрана',
        screenshotFailed: 'Не удалось сделать снимок экрана',
    },
    uk: {
        emoji: 'Емодзі',
        emojiGroups: ['Смайли', 'Люди', 'Тварини й природа', 'Їжа', 'Заняття', 'Подорожі', 'Предмети', 'Символи'],
        record: 'Утримуйте, щоб записати голосове',
        recordCancelHint: 'Відпустіть поза полем, щоб скасувати',
        recordSlideHint: 'Проведіть ліворуч, щоб скасувати',
        recordCancel: 'Скасувати',
        recordStop: 'Зупинити запис',
        recordHold: 'Утримуйте кнопку, щоб записати голосове',
        micDenied: 'Мікрофон не знайдено або доступ до нього заборонено',
        voiceFailed: 'Голосові повідомлення вимкнено',
        deleteVoice: 'Видалити запис',
        play: 'Відтворити',
        pause: 'Пауза',
        voiceMessage: 'Голосове повідомлення',
        retryUpload: 'Завантажити ще раз',
        openPicture: 'Відкрити зображення',
        download: 'Завантажити',
        previous: 'Назад',
        next: 'Вперед',
        attachFile: 'Файл',
        screenshot: 'Знімок екрана',
        screenshotFailed: 'Не вдалося зробити знімок екрана',
    },
    es: {
        emoji: 'Emoji',
        emojiGroups: ['Caras', 'Personas', 'Animales y naturaleza', 'Comida', 'Actividades', 'Viajes', 'Objetos', 'Símbolos'],
        record: 'Mantén pulsado para grabar un mensaje de voz',
        recordCancelHint: 'Suelta fuera de este campo para cancelar',
        recordSlideHint: 'Desliza a la izquierda para cancelar',
        recordCancel: 'Cancelar',
        recordStop: 'Detener la grabación',
        recordHold: 'Mantén pulsado el botón para grabar un mensaje de voz',
        micDenied: 'No se encontró el micrófono o no hay permiso para usarlo',
        voiceFailed: 'Los mensajes de voz están desactivados',
        deleteVoice: 'Eliminar la grabación',
        play: 'Reproducir',
        pause: 'Pausa',
        voiceMessage: 'Mensaje de voz',
        retryUpload: 'Volver a subir',
        openPicture: 'Abrir imagen',
        download: 'Descargar',
        previous: 'Anterior',
        next: 'Siguiente',
        attachFile: 'Archivo',
        screenshot: 'Captura de pantalla',
        screenshotFailed: 'No se pudo hacer la captura de pantalla',
    },
    id: {
        emoji: 'Emoji',
        emojiGroups: ['Wajah', 'Orang', 'Hewan dan alam', 'Makanan', 'Aktivitas', 'Perjalanan', 'Benda', 'Simbol'],
        record: 'Tahan untuk merekam pesan suara',
        recordCancelHint: 'Lepaskan di luar kolom ini untuk membatalkan',
        recordSlideHint: 'Geser ke kiri untuk membatalkan',
        recordCancel: 'Batal',
        recordStop: 'Hentikan rekaman',
        recordHold: 'Tahan tombol untuk merekam pesan suara',
        micDenied: 'Mikrofon tidak ditemukan atau tidak diizinkan',
        voiceFailed: 'Pesan suara dinonaktifkan',
        deleteVoice: 'Hapus rekaman',
        play: 'Putar',
        pause: 'Jeda',
        voiceMessage: 'Pesan suara',
        retryUpload: 'Unggah lagi',
        openPicture: 'Buka gambar',
        download: 'Unduh',
        previous: 'Sebelumnya',
        next: 'Berikutnya',
        attachFile: 'File',
        screenshot: 'Tangkapan layar',
        screenshotFailed: 'Tidak dapat mengambil tangkapan layar',
    },
    lt: {
        emoji: 'Jaustukai',
        emojiGroups: ['Veidukai', 'Žmonės', 'Gyvūnai ir gamta', 'Maistas', 'Veikla', 'Kelionės', 'Daiktai', 'Simboliai'],
        record: 'Palaikykite, kad įrašytumėte balso žinutę',
        recordCancelHint: 'Atleiskite už šio lauko, kad atšauktumėte',
        recordSlideHint: 'Perbraukite kairėn, kad atšauktumėte',
        recordCancel: 'Atšaukti',
        recordStop: 'Sustabdyti įrašymą',
        recordHold: 'Palaikykite mygtuką, kad įrašytumėte balso žinutę',
        micDenied: 'Mikrofonas nerastas arba prieiga prie jo neleidžiama',
        voiceFailed: 'Balso žinutės išjungtos',
        deleteVoice: 'Ištrinti įrašą',
        play: 'Groti',
        pause: 'Pristabdyti',
        voiceMessage: 'Balso žinutė',
        retryUpload: 'Įkelti dar kartą',
        openPicture: 'Atidaryti paveikslėlį',
        download: 'Atsisiųsti',
        previous: 'Ankstesnis',
        next: 'Kitas',
        attachFile: 'Failas',
        screenshot: 'Ekrano kopija',
        screenshotFailed: 'Nepavyko padaryti ekrano kopijos',
    },
    pt: {
        emoji: 'Emojis',
        emojiGroups: ['Caras', 'Pessoas', 'Animais e natureza', 'Comida', 'Atividades', 'Viagens', 'Objetos', 'Símbolos'],
        record: 'Mantenha premido para gravar uma mensagem de voz',
        recordCancelHint: 'Solte fora deste campo para cancelar',
        recordSlideHint: 'Deslize para a esquerda para cancelar',
        recordCancel: 'Cancelar',
        recordStop: 'Parar a gravação',
        recordHold: 'Mantenha o botão premido para gravar uma mensagem de voz',
        micDenied: 'Microfone não encontrado ou sem permissão',
        voiceFailed: 'As mensagens de voz estão desativadas',
        deleteVoice: 'Eliminar a gravação',
        play: 'Reproduzir',
        pause: 'Pausa',
        voiceMessage: 'Mensagem de voz',
        retryUpload: 'Carregar novamente',
        openPicture: 'Abrir imagem',
        download: 'Transferir',
        previous: 'Anterior',
        next: 'Seguinte',
        attachFile: 'Ficheiro',
        screenshot: 'Captura de ecrã',
        screenshotFailed: 'Não foi possível fazer a captura de ecrã',
    },
    zhcn: {
        emoji: '表情',
        emojiGroups: ['笑脸', '人物', '动物与自然', '食物', '活动', '旅行', '物品', '符号'],
        record: '按住录制语音消息',
        recordCancelHint: '在此区域外松开即可取消',
        recordSlideHint: '向左滑动取消',
        recordCancel: '取消',
        recordStop: '停止录音',
        recordHold: '请按住按钮录制语音消息',
        micDenied: '未找到麦克风或未获允许',
        voiceFailed: '语音消息已关闭',
        deleteVoice: '删除录音',
        play: '播放',
        pause: '暂停',
        voiceMessage: '语音消息',
        retryUpload: '重新上传',
        openPicture: '打开图片',
        download: '下载',
        previous: '上一张',
        next: '下一张',
        attachFile: '文件',
        screenshot: '截屏',
        screenshotFailed: '无法截屏',
    },
    zhtw: {
        emoji: '表情符號',
        emojiGroups: ['笑臉', '人物', '動物與自然', '食物', '活動', '旅行', '物品', '符號'],
        record: '按住以錄製語音訊息',
        recordCancelHint: '在此區域外放開即可取消',
        recordSlideHint: '向左滑動以取消',
        recordCancel: '取消',
        recordStop: '停止錄音',
        recordHold: '請按住按鈕錄製語音訊息',
        micDenied: '找不到麥克風或未獲允許',
        voiceFailed: '語音訊息已關閉',
        deleteVoice: '刪除錄音',
        play: '播放',
        pause: '暫停',
        voiceMessage: '語音訊息',
        retryUpload: '重新上傳',
        openPicture: '開啟圖片',
        download: '下載',
        previous: '上一張',
        next: '下一張',
        attachFile: '檔案',
        screenshot: '螢幕截圖',
        screenshotFailed: '無法擷取螢幕畫面',
    },
};
